import { Clear } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { typingTimeOut } from '../../constants';
import { TalentStatus } from '../../enums/talentStatus';
import { TalentType } from '../../enums/talentType';
import { Talent } from '../../models/talent';
import { talentService } from '../../services/talentService';

export interface FilterTalentsForm {
  talents?: Talent[];
  accountType?: string;
  city?: string;
  state?: string;
  status?: string[];
}

interface Props {
  handleFormSubmit: (data: FilterTalentsForm) => void;
  filterData: FilterTalentsForm;
}

const defaultValues: FilterTalentsForm = {
  talents: [],
  accountType: '',
  city: '',
  state: '',
  status: []
};

const FilterTalents = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterTalentsForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);

  useEffect(() => {
    getTalents();
  }, []);

  const getTalents = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              id="talent"
              options={talents}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={filterData.talents ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  talents: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Talent" />}
            />
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="accountType">Account Type</InputLabel>
              <Select
                labelId="accountType"
                label="accountType"
                name="accountType"
                value={filterData.accountType}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Account Type" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.keys(TalentType).map((talentType) => (
                  <MenuItem key={talentType} value={talentType}>
                    {TalentType[talentType as keyof typeof TalentType]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ maxWidth: 245 }}
              label="City"
              name="city"
              value={filterData.city}
              onChange={handleChange}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="State"
              name="state"
              value={filterData.state}
              onChange={handleChange}
            />
            <Autocomplete
              multiple
              id="status"
              options={Object.keys(TalentStatus)}
              getOptionLabel={(option) => {
                const key = option as keyof typeof TalentStatus;
                return TalentStatus[key];
              }}
              value={filterData.status ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  status: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterTalents;
